/* eslint-disable max-lines-per-function */
import Table, { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Broadcast, CriticalError, CriticalErrorCodes } from '../types';
import { Button, Space } from 'antd';
import { AuthContextProps, withAuth } from '../LocalAuthWrapper';
import { CAPTION_RTMP_API_URL } from '../config';
import CreateBroadcastForm from './CreateBroadcastForm';
import { InfoCircleOutlined, VideoCameraAddOutlined } from '@ant-design/icons';
import Title from 'antd/es/typography/Title';
import { Link } from 'react-router-dom';
import BroadcastStateIcon from './BroadcastStateIcon';
import { StateCode } from '../BroadcastStateCodes';
import { isBroadcastArray } from '../typeguards';
import './BroadcastListTable.css';

type BroadcastListTableProps = {
  userId: string;
} & AuthContextProps;

const BroadCastListTable: React.FC<BroadcastListTableProps> = (props) => {
  const [broadcastList, setBroadcastList] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [criticalError, setCriticalError] = useState<CriticalError>({
    wasThrown: false,
    logMessage: '',
    userMessage: undefined,
    userDetails: undefined,
  });

  const fetchBroadcastData = async (showLoading: boolean) => {
    showLoading && setLoading(true);

    const handleGetError = (logMsg: string, userMsg: string, userDetails: string, code?: CriticalErrorCodes) => {
      showLoading && setLoading(false);
      setCriticalError({
        wasThrown: true,
        code: code,
        logMessage: logMsg,
        userMessage: userMsg,
        userDetails: userDetails,
      });
    };

    try {
      const response = await fetch(`${CAPTION_RTMP_API_URL}/broadcasts/${props.userId}`, {
        headers: { authorization: `Bearer ${props.authCtx.accessToken}` },
      });

      const getErrLogMsg: string = 'Unexpected Broadcast data format received from server.';
      const getErrUserMsg: string = 'Úbbs! Óvænt villa kom upp.';
      const getErrUserDetails: string = 'Við munum gera okkar besta til að fyrirbyggja að þetta gerist aftur.';
      if (!response.headers.get('content-type')?.includes('application/json')) {
        handleGetError(getErrLogMsg, getErrUserMsg, getErrUserDetails, 'INCORRECT_DATA_FORMAT');
        return;
      }

      const json = await response.json();
      if (!isBroadcastArray(json)) {
        handleGetError(getErrLogMsg, getErrUserMsg, getErrUserDetails, 'INCORRECT_DATA_FORMAT');
        return;
      }

      if (json.some((broadcast: Broadcast) => !broadcast.state)) {
        // This is a temporary way of handling this until we make the page fully compatible with older stateless versions of Kappi.
        handleGetError(getErrLogMsg, getErrUserMsg, getErrUserDetails, 'STATE_INCOMPATIBLE_SERVER');
        return;
      }

      const data = json.map((row: any) => ({
        key: row.id,
        ...row,
      }));
      setBroadcastList(data);
      showLoading && setLoading(false);
    } catch (e) {
      console.error(e);
      handleGetError('Failed to fetch broadcast data.', 'Gat ekki sótt gögn.', '');
    }
  };

  useEffect(() => {
    // Initial data fetch.
    fetchBroadcastData(true);
  }, []);

  useEffect(() => {
    if (!showForm) {
      // Reload data when form is closed.
      fetchBroadcastData(true);
    }
  }, [showForm]);

  useEffect(() => {
    // Periodic data fetches (updates).
    const updateInterval = window.setInterval(async () => {
      if (criticalError.wasThrown) {
        console.log(
          `BROADCAST: ${
            criticalError.code === 'NOT_FOUND'
              ? 'Not found, '
              : criticalError.code === 'INCORRECT_DATA_FORMAT'
              ? 'Incorrect data format, '
              : criticalError.code === 'STATE_INCOMPATIBLE_SERVER'
              ? 'Current server version does not support broadcast states, '
              : 'Unknown error, '
          }cancelling updates...`,
        );
        clearInterval(updateInterval);
        return;
      }
      await fetchBroadcastData(false);
      console.log('Periodic table update...');
    }, 5000);

    return () => {
      if (updateInterval) {
        console.log('TABLE: Unmounting and clearing interval');
        clearInterval(updateInterval);
      }
    };
  }, [props.authCtx.accessToken, props.userId, criticalError.wasThrown, criticalError.code]);

  const formatTime = (timeString: string) => {
    return dayjs(timeString).format('DD. MMM YYYY - HH:mm');
  };

  const tableHeaderContainer = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '-15px',
  };

  const getColumns = (archived: boolean) => {
    const columns: ColumnsType<Broadcast> = [
      {
        title: 'Heiti',
        shouldCellUpdate: () => false,
        dataIndex: 'display_name',
        key: 'display_name',
        render: (text, broadcast) => (
          <b style={{ opacity: ['CANCELLED', 'ERROR'].includes(broadcast.state.code) ? 0.25 : 1 }}>{text}</b>
        ),
        sorter: (a, b) => a.display_name.localeCompare(b.display_name),
      },
      {
        title: 'Áfangastaður',
        shouldCellUpdate: () => false,
        dataIndex: 'destination',
        key: 'destination',
        render: (text, broadcast) => (
          <div style={{ opacity: ['CANCELLED', 'ERROR'].includes(broadcast.state.code) ? 0.25 : 1 }}>{text}</div>
        ),
      },
      {
        title: 'Skráningartími',
        shouldCellUpdate: () => false,
        dataIndex: 'created_at',
        key: 'created_at',
        render: (text, broadcast) => (
          <div style={{ opacity: ['CANCELLED', 'ERROR'].includes(broadcast.state.code) ? 0.25 : 1 }}>
            {formatTime(text)}
          </div>
        ),
        defaultSortOrder: `${archived ? 'descend' : 'ascend'}`,
        sorter: (a, b) => dayjs(a.created_at).diff(dayjs(b.created_at)),
      },
      {
        title: `Útsending ${archived ? 'hófst' : 'hefst'}`,
        shouldCellUpdate: () => false,
        key: 'est_start_time',
        dataIndex: 'est_start_time',
        render: (text, broadcast) => (
          <div style={{ opacity: ['CANCELLED', 'ERROR'].includes(broadcast.state.code) ? 0.25 : 1 }}>
            {formatTime(text)}
          </div>
        ),
        sorter: (a, b) => dayjs(a.est_start_time).diff(dayjs(b.est_start_time)),
      },
      {
        title: `Útsendingu ${archived ? 'lauk' : 'lýkur'}`,
        shouldCellUpdate: () => false,
        key: 'est_end_time',
        dataIndex: 'est_end_time',
        render: (text, broadcast) => (
          <div style={{ opacity: ['CANCELLED', 'ERROR'].includes(broadcast.state.code) ? 0.25 : 1 }}>
            {formatTime(text)}
          </div>
        ),
        sorter: (a, b) => dayjs(a.est_end_time).diff(dayjs(b.est_end_time)),
      },
      {
        title: 'Staða',
        shouldCellUpdate: (record, prevRecord) => record.state?.code !== prevRecord.state?.code,
        key: 'state',
        dataIndex: 'state',
        align: 'center',
        width: 85,
        render: (stateInfo, broadcast) => (
          <BroadcastStateIcon
            stateInfo={stateInfo}
            broadcastTimeframe={{ estStartTime: broadcast.est_start_time, estEndTime: broadcast.est_end_time }}
            displayHelp={true}
            size='small'
          />
        ),
      },
      {
        key: 'details',
        shouldCellUpdate: () => false,
        dataIndex: 'id',
        width: 105,
        render: (text) => (
          <Link to={`/rauntimatextun/broadcasts/${text}`}>
            <Button icon={<InfoCircleOutlined />}>Nánar</Button>
          </Link>
        ),
      },
    ];
    return columns;
  };

  // TODO(Smári, RAUN-212): Finna út hvert á að setja villustöður. Væri helst til í að sjá þær alltaf efst í archived...?
  const futureAndCurrentBroadcastStates: StateCode[] = [
    StateCode.scheduled,
    StateCode.active,
    StateCode.streaming,
    StateCode.overstreaming,
  ];
  const futureAndCurrentBroadcasts: Broadcast[] = broadcastList.filter((broadcast) =>
    futureAndCurrentBroadcastStates.includes(broadcast.state.code),
  );

  // The rest (set difference) are archived broadcasts.
  const archivedBroadcasts: Broadcast[] = broadcastList.filter(
    (broadcast) => !futureAndCurrentBroadcasts.map((b) => b.id).includes(broadcast.id),
  );

  // A sanity-check: Make sure all broadcasts are displayed exactly once.
  const listItemCount: number = futureAndCurrentBroadcasts.length + archivedBroadcasts.length;
  if (listItemCount !== broadcastList.length) {
    listItemCount < broadcastList.length && console.warn('Some broadcasts are not displayed!');
    listItemCount > broadcastList.length && console.warn('Some broadcasts are displayed more than once!');
  }

  if (criticalError.wasThrown) {
    console.error(`Critical error: ${criticalError.logMessage} - Code: ${criticalError.code}`);
    return (
      <div className='container-wrapper'>
        <div className='details-container'>
          <Space direction='vertical'>
            <Title>
              {criticalError.userMessage ? criticalError.userMessage : 'Úbbs, villa! Þetta eru nú meiri leiðindin.'}
            </Title>
            <Title level={2}>
              {criticalError.userDetails ? criticalError.userDetails : 'Okkur þykir þetta afskaplega leiðinlegt.'}
            </Title>
          </Space>
          <Title level={3}>
            Afsakaðu þetta. Ef þetta er að gerast ítrekað skaltu hiklaust hafa samband við
            <a href='mailto:tiro@tiro.is'> tiro@tiro.is</a>.
          </Title>
          <Link to={'/'}>
            <Button id='broadcast-details-back-btn' type='default'>
              Til baka
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <CreateBroadcastForm userId={props.userId} show={showForm} toggleShow={() => setShowForm(!showForm)} />
      <div style={tableHeaderContainer}>
        <Title level={4}>Á döfinni</Title>
        <Button onClick={() => setShowForm(!showForm)} icon={<VideoCameraAddOutlined />}>
          Ný útsending
        </Button>
      </div>
      <Space direction='vertical' size={'middle'}>
        <Space direction='vertical' align='start'>
          <Table
            className='broadcast-list-table'
            columns={getColumns(false)}
            dataSource={futureAndCurrentBroadcasts}
            scroll={{ y: '50vh' }}
            loading={loading}
            size='middle'
            locale={{ emptyText: 'Það eru engar útsendingar á döfinni hjá þér...' }}
          />
        </Space>
        <Space direction='vertical' align='start'>
          <Title level={4}>Liðnar</Title>
          <Table
            className='broadcast-list-table-archived'
            columns={getColumns(true)}
            dataSource={archivedBroadcasts}
            pagination={{ pageSize: 10 }}
            scroll={{ y: '50vh' }}
            loading={loading}
            size='middle'
            locale={{ emptyText: 'Liðnar útsendingar munu birtast hér' }}
          />
        </Space>
      </Space>
    </div>
  );
};

export default withAuth(BroadCastListTable);
